"use client";

import { useState, useEffect } from "react";

export interface LocalizationWarning {
  flag: string;
  title: string;
  country: string;
  hyperlink: string;
  countryCode: string;
  description: string;
}

export const localizationWarningData: LocalizationWarning[] = [
  {
    country: "ofac_sanctioned",
    countryCode: "OFAC",
    title: "Camp services are not available in your region",
    description:
      "Camp Network services are restricted in OFAC sanctioned regions",
    hyperlink: "https://www.campnetwork.xyz/",
    flag: "🚫",
  },
  {
    country: "european_union",
    countryCode: "EU",
    title: "Camp services are not available in your region",
    description:
      "Camp Network services are currently restricted in EU/EEA regions",
    hyperlink: "https://www.campnetwork.xyz/",
    flag: "🇪🇺",
  },
  {
    country: "balkans",
    countryCode: "BALKANS",
    title: "Camp services are not available in your region",
    description:
      "Camp Network services are currently restricted in Balkans regions",
    hyperlink: "https://www.campnetwork.xyz/",
    flag: "🏴",
  },
  {
    country: "restricted",
    countryCode: "RESTRICTED",
    title: "Camp services are not available in your region",
    description:
      "Camp Network services are currently restricted in your region",
    hyperlink: "https://www.campnetwork.xyz/",
    flag: "⚠️",
  },
];

// Interface for geolocation API response
interface GeolocationData {
  ip: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  isRestricted: boolean;
  restrictionType: string | null;
  coordinates: {
    lat: number;
    lon: number;
  };
  error?: string;
}

export const useCountryDetection = () => {
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);

  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Fetch IP-based geolocation data
        const response = await fetch("/api/geolocation");
        if (!response.ok) {
          throw new Error("Failed to fetch geolocation");
        }

        const data: GeolocationData = await response.json();

        if (data.isRestricted && data.restrictionType) {
          setDetectedCountry(data.restrictionType);
        } else {
          setDetectedCountry(null);
        }
      } catch (error) {
        console.error("Country detection failed:", error);
        // In production, geolocation failure should result in restricted access
        // as we cannot verify the user's location
        setDetectedCountry("RESTRICTED");
      }
    };

    detectCountry();
  }, []);

  return detectedCountry;
};

export const getWarningByCountryCode = (
  countryCode: string | null,
): LocalizationWarning | null => {
  if (!countryCode) return null;

  return (
    localizationWarningData.find(
      (warning) =>
        warning.countryCode.toLowerCase() === countryCode.toLowerCase(),
    ) || null
  );
};

export const isRestrictedRegion = (countryCode: string | null): boolean => {
  return (
    countryCode === "OFAC" ||
    countryCode === "EU" ||
    countryCode === "BALKANS" ||
    countryCode === "RESTRICTED"
  );
};

// Hook to get detected country
export const useEffectiveCountry = () => {
  return useCountryDetection();
};

// Hook to check if current user is in restricted region
export const useIsRestrictedRegion = () => {
  const effectiveCountry = useEffectiveCountry();
  return isRestrictedRegion(effectiveCountry);
};

// Hook to get warning data for current user
export const useWarningData = () => {
  const effectiveCountry = useEffectiveCountry();
  return getWarningByCountryCode(effectiveCountry);
};
